import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import DatePicker from 'react-datepicker';
import isValid from 'date-fns/isValid';
import format from 'date-fns/format';
import parse from 'date-fns/parse';

import { isRequired } from 'components/Questionnaires/validators';

export const QuestionDatePickerField = ({ intl, blockId, question, className, onChange }) => {
  const pickerConfig = useMemo(() => Object.assign({
    popperPlacement: 'bottom-start',
    popperModifiers: [],
    isClearable: false,
    preventKeyboardDisplay: false,
    dateFormat: 'yyyy-MM-dd',
    maxDate: new Date()
  }, question?.componentProps?.pickerConfig ?? {}), [question.componentProps?.pickerConfig]);
  const datePicker = React.useRef(null);

  const defaultDate = () => {
    const date = question?.answer ?? question.default ?? '';
    const parsed = parse(date, pickerConfig.dateFormat, new Date());
    return isValid(parsed) ? parsed : '';
  };

  const [inputValue, setInputValue] = useState(defaultDate());

  const onInputChange = (value) => {
    const formattedValue = format(value, pickerConfig.dateFormat);
    setInputValue(value);
    onChange(question, formattedValue);
    return true;
  };

  useEffect(() => {
    if (inputValue) {
      const formattedValue = format(inputValue, pickerConfig.dateFormat);
      onChange(question, formattedValue);
    }
  }, []);

  const isTouchDevice = () => {
    try {
      const prefixes = ' -webkit- -moz- -o- -ms- '.split(' ');
      const mq = function (query) {
        return window.matchMedia(query).matches;
      };

      if (('ontouchstart' in window) || (typeof window.DocumentTouch !== 'undefined' && document instanceof window.DocumentTouch)) {
        return true;
      }

      return mq(['(', prefixes.join('touch-enabled),('), 'testtouch', ')'].join(''));
    } catch (e) {
      return false;
    }
  };

  const isIOS = () => !!navigator.platform && /iPad|iPhone|iPod/.test(navigator.platform);

  const onHandleKeyDown = (e) => {
    if (e.keyCode === 8) {
      setInputValue(null);
      onChange(question, '');
    }
  };

  const handleFocus = (e) => {
    if (isTouchDevice() && pickerConfig.preventKeyboardDisplay && datePicker != null && datePicker.current != null && datePicker.current.input != null) {
      // Switching the reaadonly property of the input prevents the keyboard from display on touch devices.
      if (!datePicker.current.input.readOnly && isIOS()) {
        datePicker.current.input.blur();
      }
      datePicker.current.input.readOnly = true;
    }
  };

  const handleBlur = (e) => {
    if (isTouchDevice() && !isIOS() && pickerConfig.preventKeyboardDisplay && datePicker != null && datePicker.current != null && datePicker.current.input != null) {
      // Switching the reaadonly property of the input prevents the keyboard from display on touch devices.
      datePicker.current.input.readOnly = false;
    }
  };

  return (
    <span id={ question.id } className={ `${ className }${ !question.isValid ? ' error' : '' }` }>
      <DatePicker
        key={ `${ blockId }-${ question.id }-datepicker` }
        id={ `${ blockId }-${ question.id }-datepicker` }
        name={ question.id }
        ref={ datePicker }
        placeholder={ question.placeholder ?? question.label ? intl.formatMessage({ id: question.placeholder ?? question.label }) : '' }
        fixedHeight
        showMonthDropdown
        placeholderText={ question.placeholder ?? question.label ? intl.formatMessage({ id: question.placeholder ?? question.label }) : '' }
        maxDate={ pickerConfig.maxDate }
        popperPlacement={ pickerConfig.popperPlacement }
        popperModifiers={ pickerConfig.popperModifiers }
        autoComplete="off"
        isClearable={ pickerConfig.isClearable }
        showYearDropdown
        dropdownMode="select"
        className={ className === '' ? 'form-select' : className }
        dateFormat={ pickerConfig.dateFormat && pickerConfig.dateFormat.length > 0 ? pickerConfig.dateFormat : 'yyyy-MM-dd' }
        selected={ inputValue }
        onKeyDown={ e => onHandleKeyDown(e) }
        onChange={ (date) => { onInputChange(date); } }
        onFocus={ e => handleFocus(e) }
        onBlur={ e => handleBlur(e) }
        wrapperClassName="questionnaire-datepicker"
        disabled={ question.disabled }
        required={ isRequired(question) }
        ariaRequired={ isRequired(question) }
        aria-describedby={ question.isValid === false ? (`${ blockId }-${ question.id }-error`) : undefined }
      />
    </span>
  );
};

QuestionDatePickerField.displayName = 'QuestionDatePickerField';

QuestionDatePickerField.propTypes = {
  intl: PropTypes.shape().isRequired,
  blockId: PropTypes.string.isRequired,
  question: PropTypes.shape().isRequired,
  className: PropTypes.string,
  onChange: PropTypes.func.isRequired
};

QuestionDatePickerField.defaultProps = {
  className: '',
};

export default QuestionDatePickerField;
